import {
	action,
	observable,
	makeObservable,
	computed,
	reaction,
	runInAction,
} from 'mobx';
import {enumToValues} from '@netx/core-next/utils/enum';
import {RuleModel} from '../baseModel';
import {
	FileAspectType,
	FileAspectSubType,
	FileAspectOrientationValue,
	SearchTypeFileAspect,
	TSearchTypeFileAspect,
} from './constants';
import {fileAspectRuleDefaults} from './defaults';
import type {FileAspectRule} from './types';
import {validateFileAspectRule} from './validate';

const getFileAspectSubTypeOptions = (fileAspectType: FileAspectType) => {
	if (fileAspectType !== FileAspectType.ORIENTATION)
		return enumToValues(FileAspectSubType);
	return [FileAspectSubType.EQUAL_TO];
};

export class FileAspectRuleModel
	extends RuleModel<TSearchTypeFileAspect>
	implements FileAspectRule
{
	readonly elementType = SearchTypeFileAspect;
	elementSubType1: FileAspectType = fileAspectRuleDefaults.elementSubType1;
	elementSubType2: FileAspectSubType = fileAspectRuleDefaults.elementSubType2;
	elementValue1: number = fileAspectRuleDefaults.elementValue1;
	elementValue2: '' = fileAspectRuleDefaults.elementValue2;
	elementValue3: '' = fileAspectRuleDefaults.elementValue3;

	get elementSubType1Options() {
		return enumToValues(FileAspectType);
	}

	get elementSubType2Options() {
		return getFileAspectSubTypeOptions(this.elementSubType1);
	}

	get isValid() {
		const validation = validateFileAspectRule(this);
		this.setValidationErrors(this.dirty && validation ? validation : undefined);
		if (!validation) return true;
		return false;
	}

	constructor(params?: Partial<Omit<FileAspectRule, 'elementType'>>) {
		super();

		runInAction(() => {
			Object.assign(this, {...fileAspectRuleDefaults, ...params});
		});

		makeObservable(this, {
			elementSubType1: observable,
			elementSubType1Options: computed,
			elementSubType2: observable,
			elementSubType2Options: computed,
			elementValue1: observable,
			isValid: computed,
			setElementSubType1: action,
			setElementSubType2: action,
			setElementValue1: action,
		});

		this.reactionDisposers.push(
			reaction(
				() => this.elementSubType2Options,
				(elementSubType2Options) => {
					if (!elementSubType2Options.includes(this.elementSubType2)) {
						this.setElementSubType2(elementSubType2Options[0]);
					}
				},
			),
		);

		this.reactionDisposers.push(
			reaction(
				() => this.elementSubType1,
				(elementSubType1, prevElementSubType1) => {
					if (elementSubType1 === FileAspectType.ORIENTATION) {
						this.elementValue1 = FileAspectOrientationValue.LANDSCAPE;
						return;
					}

					if (prevElementSubType1 === FileAspectType.ORIENTATION) {
						// set to 0 for input value
						this.elementValue1 = 0;
						return;
					}
				},
			),
		);
	}

	/**
	 * Set file aspect type
	 * @param elementSubType1 FileAspectType
	 */
	setElementSubType1(elementSubType1: FileAspectType) {
		this.elementSubType1 = elementSubType1;
	}

	/**
	 * Set file aspect compute type
	 * @param elementSubType2 FileAspectSubType
	 */
	setElementSubType2(elementSubType2: FileAspectSubType) {
		this.elementSubType2 = elementSubType2;
	}

	/**
	 * Set file aspect
	 * @param elementValue1 number | FileAspectOrientationValue
	 */
	setElementValue1(elementValue1: number | FileAspectOrientationValue) {
		this.elementValue1 = elementValue1;
	}

	/** stubbed */
	setElementValue2() {}

	/** stubbed */
	setElementValue3() {}
}

export default FileAspectRuleModel;
